import React from "react";
import { createPortal } from "react-dom";
import { CalendarDays, MapPinCheck } from "lucide-react";
import { fetchGeotabOrdersReport } from "../api/geotabApi.js";
import { ActionButton } from "./ResultsPrimitives.jsx";
import ReportsDisabledActionHoverCard from "./ReportsDisabledActionHoverCard.jsx";
import { Rows } from "./resultsPageChrome/DispatchGeotabOrders.jsx";
import { downloadGeotabOrdersReport, markExportDocumentWindowFailed, openExportDocumentWindow } from "../lib/exportApi.js";
import { getLocalTodayDispatchDate } from "../utils/dispatchDayCalendarUtils.js";
import { buildOrdersPeriodScopeLabel, shouldResyncOrdersReportDateRange } from "../utils/buildDataManagerOrdersExportViewModel.js";
import { concordText } from "../utils/dispatchGeotabOrders.js";
import "./resultsPageChrome/DispatchGeotabGapView.css";

// Les mêmes lignes que la liste Geotab du Dispatch, datées : les commandes à vérifier dans l'ordre du serveur, une phrase et
// une piste chacune ; celles où le bon camion s'est arrêté sont repliées.
const toRow = (item) => ({ kind: item.status, truck: item.planned_truck || "", driver: "", order: item });

export default function ReportsGeotabOrdersCard({ dispatchDate = "" }) {
  const [dateFrom, setDateFrom] = React.useState(dispatchDate);
  const [dateTo, setDateTo] = React.useState(dispatchDate);
  const previousDispatchDateRef = React.useRef(dispatchDate);
  const [period, setPeriod] = React.useState("day");
  const [shown, setShown] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const [showFound, setShowFound] = React.useState(false);
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const titleId = React.useId();
  const today = getLocalTodayDispatchDate();
  const disabledReason = !dateFrom || !dateTo ? "Sélectionnez une date de début et une date de fin."
    : dateFrom > dateTo ? "La date de fin doit être égale ou postérieure à la date de début."
    : dateTo > today ? "Geotab ne connaît pas encore cette date."
    : (Date.parse(dateTo) - Date.parse(dateFrom)) / 86400000 >= 31 ? "Sélectionnez une période de 31 jours maximum." : "";

  React.useEffect(() => {
    if (dispatchDate && shouldResyncOrdersReportDateRange({ previousDispatchDate: previousDispatchDateRef.current, dateFrom, dateTo })) {
      setDateFrom(dispatchDate);
      setDateTo(dispatchDate);
    }
    previousDispatchDateRef.current = dispatchDate;
  }, [dispatchDate, dateFrom, dateTo]);

  React.useEffect(() => {
    if (!shown) return undefined;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    closeRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [shown]);

  React.useEffect(() => {
    setFeedback(null);
    setIsLoading(false);
    if (!shown || disabledReason) return undefined;
    let active = true;
    setIsLoading(true);
    fetchGeotabOrdersReport(dateFrom, period, dateTo)
      .then((result) => { if (active) setReport(result); })
      .catch((error) => {
        if (!active) return;
        setReport(null);
        setFeedback({ error: true, message: error.message });
      })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [shown, dateFrom, dateTo, period, disabledReason]);

  const generate = async () => {
    setIsGenerating(true);
    setFeedback(null);
    const scopeLabel = `${buildOrdersPeriodScopeLabel({ dateFrom, dateTo })} · ${period === "evening" ? "Soir" : "Jour"}`;
    let documentWindow;
    try {
      documentWindow = openExportDocumentWindow({
        detail: scopeLabel, eyebrow: "Comparatif Geotab", message: "Comparaison des commandes du Data Manager avec les arrêts Geotab.",
        title: "Préparation du comparatif Geotab", windowName: "grain-geotab-orders-report",
      });
      await downloadGeotabOrdersReport({ dateFrom, dateTo, dispatchPeriod: period, documentWindow });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Le rapport n’a pas pu être généré.";
      if (documentWindow) markExportDocumentWindowFailed(documentWindow, {
        detail: message, eyebrow: "Comparatif Geotab", message: "Le rapport n’a pas pu être généré.", title: "Export interrompu",
      });
      setFeedback({ error: true, message });
    } finally {
      setIsGenerating(false);
    }
  };

  // Le comparatif d'une autre date ou période ne reste pas affiché pendant que le nouveau se calcule.
  const current = shown && !disabledReason && report?.date_from === dateFrom && report?.date_to === dateTo && report?.period === period ? report : null;
  const rows = (current?.items || []).map(toRow);
  const doubtful = rows.filter((row) => row.kind !== "found");
  const found = rows.filter((row) => row.kind === "found");
  const concord = concordText({ ordersRead: true, total: current?.total || 0, found });
  return <article className="reports-card" data-geotab-orders-report-card>
    <div className="reports-card-header">
      <div className="reports-card-icon"><MapPinCheck size={25} aria-hidden="true" /></div>
      <div className="grid gap-0.5"><h2 className="reports-card-title text-slate-950">Comparatif Geotab</h2>
        <p className="text-[0.82rem] leading-[1.35] text-slate-600">Les commandes du Data Manager face aux arrêts vus par Geotab.</p></div>
    </div>
    <div className="reports-card-body">
      <div className="reports-orders-filters">
        <label className="reports-field">
          <span className="reports-label inline-flex items-center gap-2"><CalendarDays size={14} aria-hidden="true" />Date de début</span>
          <input type="date" value={dateFrom} max={today} onChange={(event) => setDateFrom(event.target.value)}
            className="h-6 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2 py-0 text-sm font-semibold text-slate-900 outline-none focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle" />
        </label>
        <label className="reports-field">
          <span className="reports-label inline-flex items-center gap-2"><CalendarDays size={14} aria-hidden="true" />Date de fin</span>
          <input type="date" value={dateTo} min={dateFrom || undefined} max={today} onChange={(event) => setDateTo(event.target.value)}
            className="h-6 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2 py-0 text-sm font-semibold text-slate-900 outline-none focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle" />
        </label>
        <label className="reports-field">
          <span className="reports-label">Période des commandes</span>
          <select aria-label="Période du comparatif Geotab" value={period} onChange={(event) => setPeriod(event.target.value)}
            className="h-6 min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2 py-0 text-sm font-semibold text-slate-900 outline-none focus:border-border-accent-muted focus:ring-2 focus:ring-border-accent-subtle">
            <option value="day">Jour</option><option value="evening">Soir</option>
          </select>
        </label>
      </div>
      {disabledReason && <p role="status" className="text-[0.82rem] text-slate-600">{disabledReason}</p>}
      <p className="text-[0.82rem] leading-5 text-slate-500">Geotab ne confirme pas une livraison : il montre qu’un camion s’est arrêté chez le client.</p>
      <div className="reports-card-actions">
        <ReportsDisabledActionHoverCard actionLabel="Afficher" disabledReason={disabledReason} fitContent>
          <ActionButton tone="optimizer" size="sm" className="reports-primary-action" onClick={() => setShown(true)}
            disabled={Boolean(disabledReason)} aria-haspopup="dialog">Afficher</ActionButton>
        </ReportsDisabledActionHoverCard>
        <ReportsDisabledActionHoverCard actionLabel="Générer le PDF" disabledReason={disabledReason || (isGenerating ? "Le PDF est en préparation." : "")} fitContent>
          <ActionButton tone="secondary" size="sm" onClick={generate} disabled={Boolean(disabledReason) || isGenerating}
            isLoading={isGenerating} loadingLabel="Génération…">Générer le PDF</ActionButton>
        </ReportsDisabledActionHoverCard>
      </div>
    </div>
    {feedback && !shown ? <p role="alert" className="rounded-[4px] border border-border-danger-subtle bg-white px-3 py-2 text-[0.82rem] text-danger">{feedback.message}</p> : null}
    {shown ? createPortal(<dialog ref={dialogRef} aria-labelledby={titleId} aria-modal="true" data-geotab-orders-report-modal data-grain-dialog-foreground="true"
      className="gg-veil gg-light m-0 h-dvh max-h-none w-screen max-w-none border-0 font-sans"
      onCancel={(event) => { event.preventDefault(); setShown(false); }}
      onMouseDown={(event) => { if (event.target === event.currentTarget) setShown(false); }}>
      <div className="gg-modal flex flex-col !overflow-hidden !rounded-[4px]">
        <header className="gg-bar flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
          <p className="m-0 min-w-0 text-[0.82rem]">
            <b id={titleId} className="mr-1 text-base">Comparatif Geotab</b>{buildOrdersPeriodScopeLabel({ dateFrom, dateTo })} · {period === "evening" ? "Soir" : "Jour"}
          </p>
          <button ref={closeRef} type="button" className="gg-btn ml-auto" onClick={() => setShown(false)} aria-label="Fermer">
            ✕ <kbd className="ml-1 text-[10px] font-normal opacity-70">Échap</kbd>
          </button>
        </header>
        <div className="gg-orders min-h-0 overflow-y-auto px-4 pb-3 pt-2.5 text-[0.82rem] text-slate-900" aria-live="polite" aria-busy={isLoading}>
          {isLoading ? <p role="status" className="m-0 text-slate-500">Comparaison des commandes avec Geotab…</p> : null}
          {feedback ? <p role="alert" className="m-0 text-[#9f2f27]">{feedback.message}</p> : null}
          {current ? <>
            <p className="m-0 text-[0.95rem] font-bold">
              {!current.total ? "Aucune commande au Data Manager pour ces dates et cette période."
                : doubtful.length ? `${doubtful.length} commande${doubtful.length > 1 ? "s" : ""} à vérifier` : "Aucune commande à vérifier."}
              {current.total ? <span className="font-normal text-slate-500"> · {concord}</span> : null}
            </p>
            {doubtful.length ? <div className="mt-2 overflow-x-auto rounded-[4px] border border-slate-300"><Rows rows={doubtful} dated /></div> : null}
            {found.length ? <>
              <button type="button" aria-expanded={showFound} onClick={() => setShowFound((value) => !value)}
                className="mt-2 flex w-full items-center gap-2 rounded-[4px] border border-dashed border-slate-300 bg-transparent px-2.5 py-1 text-left text-slate-500 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent">
                <span aria-hidden="true">{showFound ? "▾" : "▸"}</span>
                <span><b className="text-accent">✓ {found.length} commande{found.length > 1 ? "s" : ""} concord{found.length > 1 ? "ent" : "e"}</b> : le bon camion s’est arrêté chez le client · {showFound ? "Masquer" : "Afficher"}</span>
              </button>
              {showFound ? <div className="mt-1 overflow-x-auto rounded-[4px] border border-slate-300"><Rows rows={found} dated /></div> : null}
            </> : null}
          </> : null}
          <p className="mb-0 mt-1.5 text-[0.76rem] text-slate-500">Geotab ne confirme pas une livraison : il montre qu’un camion s’est arrêté chez le client.</p>
        </div>
      </div>
    </dialog>, document.body) : null}
  </article>;
}
